import { useEffect, useMemo, useState } from "react";
import GameShell from "../GameShell";

/**
 * Port Crane — Tower of London inspired.
 * 3 ships (stacks). Each holds containers stacked on top of each other.
 * Tap a ship to pick up its top container. Tap another ship to drop it on top.
 * Reach the goal configuration with the fewest moves.
 */
export default function PortCrane() {
  return (
    <GameShell gameId="port_crane" title="Guindaste do Porto">
      {({ onFinish }) => <CraneGame onFinish={onFinish} />}
    </GameShell>
  );
}

const COLORS = ["#EF4444", "#38BDF8", "#FBBF24"];
const PUZZLES = [
  // initial -> goal stacks (left to right)
  {
    initial: [[0, 1, 2], [], []], // bottom -> top
    goal: [[], [], [0, 1, 2]],
    minMoves: 7,
  },
  {
    initial: [[0, 1], [2], []],
    goal: [[2, 1, 0], [], []],
    minMoves: 5,
  },
];

function CraneGame({ onFinish }) {
  const [puzzleIdx, setPuzzleIdx] = useState(0);
  const puzzle = PUZZLES[puzzleIdx];
  const [stacks, setStacks] = useState(() => puzzle.initial.map((s) => [...s]));
  const [holding, setHolding] = useState(null); // container id from a stack
  const [holdingFrom, setHoldingFrom] = useState(null);
  const [moves, setMoves] = useState(0);
  const [errors, setErrors] = useState(0);
  const [startedAt] = useState(() => performance.now());
  const [totalMoves, setTotalMoves] = useState(0);

  const goal = puzzle.goal;
  const solved = useMemo(
    () =>
      stacks.length === goal.length &&
      stacks.every(
        (s, i) => s.length === goal[i].length && s.every((v, j) => v === goal[i][j])
      ),
    [stacks, goal]
  );

  useEffect(() => {
    if (!solved) return;
    // Next puzzle or finish
    const t = setTimeout(() => {
      const nextIdx = puzzleIdx + 1;
      if (nextIdx >= PUZZLES.length) {
        const elapsed = performance.now() - startedAt;
        const idealTotal = PUZZLES.reduce((a, p) => a + p.minMoves, 0);
        const overhead = Math.max(0, totalMoves + moves - idealTotal);
        const score = Math.max(0, Math.round(120 - overhead * 8 - errors * 5));
        onFinish({
          score,
          duration_ms: Math.round(elapsed),
          metrics: {
            moves: totalMoves + moves,
            ideal_moves: idealTotal,
            errors,
            puzzles: PUZZLES.length,
          },
        });
      } else {
        setTotalMoves((m) => m + moves);
        setPuzzleIdx(nextIdx);
        setStacks(PUZZLES[nextIdx].initial.map((s) => [...s]));
        setHolding(null);
        setHoldingFrom(null);
        setMoves(0);
      }
    }, 700);
    return () => clearTimeout(t);
  }, [solved]); // eslint-disable-line

  const onStackTap = (idx) => {
    if (holding === null) {
      // pick top
      const s = stacks[idx];
      if (s.length === 0) {
        setErrors((e) => e + 1);
        return;
      }
      const top = s[s.length - 1];
      setHolding(top);
      setHoldingFrom(idx);
      const next = stacks.map((stk, i) => (i === idx ? stk.slice(0, -1) : stk));
      setStacks(next);
    } else {
      // drop on idx
      const next = stacks.map((stk, i) => (i === idx ? [...stk, holding] : stk));
      setStacks(next);
      setHolding(null);
      setHoldingFrom(null);
      setMoves((m) => m + 1);
    }
  };

  return (
    <div className="mt-4 max-w-4xl mx-auto" data-testid="crane-area">
      <div className="text-center text-white drop-shadow mb-4">
        <div className="text-sm">
          Toque numa pilha para pegar o contêiner de cima. Toque em outra para soltá-lo.
        </div>
        <div className="text-xs opacity-80 mt-1">
          Desafio {puzzleIdx + 1} / {PUZZLES.length} · Movimentos: {moves}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Stage title="Modelo (objetivo)" stacks={goal} highlight />
        <Stage
          title="Seu porto"
          stacks={stacks}
          interactive
          onTap={onStackTap}
          holding={holding}
          holdingFrom={holdingFrom}
        />
      </div>
      {solved && <div className="text-center text-4xl mt-4 pop-in">🎉</div>}
    </div>
  );
}

function Stage({ title, stacks, interactive, onTap, holding, holdingFrom, highlight }) {
  return (
    <div className="kid-card">
      <div className="text-center font-bold text-slate-700 mb-2">{title}</div>
      <div className="flex justify-around items-end h-56 bg-sky-50 rounded-2xl p-3">
        {stacks.map((s, i) => (
          <button
            key={i}
            disabled={!interactive}
            onClick={interactive ? () => onTap(i) : undefined}
            className={`flex-1 mx-1 h-full flex flex-col-reverse items-center justify-start transition ${
              holdingFrom === i ? "ring-2 ring-amber-400" : ""
            }`}
            data-testid={interactive ? `crane-stack-${i}` : undefined}
          >
            <div className="w-full h-2 bg-slate-400 rounded-b-lg" />
            {s.map((c, j) => (
              <div
                key={j}
                className="w-14 h-10 rounded-md mb-1 shadow"
                style={{
                  background: COLORS[c],
                  border: highlight ? "2px dashed rgba(255,255,255,0.6)" : "none",
                }}
              />
            ))}
          </button>
        ))}
      </div>
      {interactive && holding !== null && (
        <div className="text-center mt-3 text-sm text-amber-600 font-semibold">
          Segurando: <span className="inline-block w-6 h-4 align-middle rounded" style={{ background: COLORS[holding] }} />
        </div>
      )}
    </div>
  );
}
